<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsServices } from "../data/getFaqsServices.js";
	import { getMainServices } from "../data/getMainServices.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getServicesWhenWeHelp } from "../data/getServicesWhenWeHelp.js";
	import type { ServicesProps } from "./ServicesProps.js";

	const { tenant } = getGlobalTemplateProps();

	const _: ServicesProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="cg[[IT služby:]] od záchrany projektu po pronájem profi týmu" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Nemáte vlastní vývojáře, potřebujete popostrčit rozběhnutý projekt nebo si jen nevíte rady? sp[[Od toho jsme tady.]]"
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Nezávazně poptat" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Ušetříme]] vám cg[[měsíce]] tápání a náklady za chybná rozhodnutí" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="„Druhý rok po sobě se ve firmě snažíme na vlastní pěst vyřešit několik IT problémů, ale postupně zjišťujeme, že na to nemáme znalosti ani kapacity. Už není čas na hrdinství, potřebujeme odbornou pomoc.“"
				/>
			</p>
			<p>
				<Formatted text="Kolikrát jen jsme tato slova v různých obměnách slyšeli…" />
			</p>
			<p>
				<Formatted
					text="sp[[Nevydávejte se cestou pokus-omyl a spolehněte se na tým našich IT specialistů.]] Ušetříme vám měsíce tápání a vysoké náklady za chybná rozhodnutí – ať už potřebujete zachránit rozjetý projekt nebo jen zkonzultovat aktuální IT problém."
				/>
			</p>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="cg[[4 IT služby]], na které se specializujeme" />
		{/snippet}
	</Section>
	<ServicesCards cards={getMainServices(tenant)} />

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="Kdy vám IT služby cg[[vytrhnou]] trn z paty" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getServicesWhenWeHelp(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Komu jsme cg[[digitalizací a automatizací]] zrychlili růst podnikání?" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.ItServices} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Časté otázky k cg[[IT službám]]" />
		{/snippet}
		<FaqItems faqs={getFaqsServices(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
